import React, { useState, useEffect, useCallback, useRef, createContext, useContext, useMemo } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import confetti from 'canvas-confetti';

// ═══════════════════════════════════════════════════════════════════════════
// STORAGE KEYS
// ═══════════════════════════════════════════════════════════════════════════
const PHASE1_COMPLETED_KEY = 'reviewer_tour_phase1_completed';
const PHASE1_SHOWN_KEY = 'reviewer_tour_phase1_shown_once';
const PHASE2_COMPLETED_KEY = 'reviewer_tour_phase2_completed';

// Context to pass dynamic tour state to the tooltip without inline functions
const TourContext = createContext({ currentPhase: 1, phaseStepCount: 6 });

// ═══════════════════════════════════════════════════════════════════════════
// CONFETTI CELEBRATION
// ═══════════════════════════════════════════════════════════════════════════
const fireConfetti = () => {
  const duration = 2000;
  const end = Date.now() + duration;
  const colors = ['#F2D857', '#FFCC00', '#FFD700', '#FFA500', '#FF6B6B', '#4ECDC4', '#45B7D1'];

  const frame = () => {
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.7 },
      colors,
      zIndex: 20000,
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.7 },
      colors,
      zIndex: 20000,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  };
  frame();
};

// ═══════════════════════════════════════════════════════════════════════════
// CSS ANIMATIONS (injected once)
// ═══════════════════════════════════════════════════════════════════════════
const STYLE_ID = 'reviewer-tour-animations';
const injectStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes tourBounceIn {
      0% { opacity: 0; transform: scale(0.96); }
      100% { opacity: 1; transform: scale(1); }
    }
    @keyframes tourPulseGlow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(242, 216, 87, 0.5); }
      50% { box-shadow: 0 0 20px 8px rgba(242, 216, 87, 0.3); }
    }
    @keyframes tourProgressFill {
      from { width: 0%; }
    }
    @keyframes tourFadeSlideUp {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes levelUpFlash {
      0% { opacity: 0; transform: scale(0.8); }
      15% { opacity: 1; transform: scale(1.05); }
      85% { opacity: 1; transform: scale(1); }
      100% { opacity: 0; transform: scale(1.1); }
    }
    .tour-tooltip-enter {
      animation: tourBounceIn 0.35s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
    }
    .tour-target-glow {
      animation: tourPulseGlow 1.5s ease-in-out infinite;
    }
    .tour-progress-fill {
      animation: tourProgressFill 0.6s ease-out forwards;
    }
    .tour-content-enter {
      animation: tourFadeSlideUp 0.3s ease-out forwards;
    }
    .tour-level-up {
      animation: levelUpFlash 2s ease-in-out forwards;
    }
    .tour-spotlight-active {
      position: relative !important;
      z-index: 15001 !important;
    }
    .tour-tabbar-elevated {
      position: relative !important;
      z-index: 15001 !important;
    }

    /* Smooth floater glide between step positions */
    .__floater {
      transition: opacity 0.35s ease, left 0.4s cubic-bezier(0.25, 0.1, 0.25, 1), top 0.4s cubic-bezier(0.25, 0.1, 0.25, 1), transform 0.4s cubic-bezier(0.25, 0.1, 0.25, 1) !important;
    }

    /* Smooth spotlight/overlay transitions */
    .react-joyride__overlay {
      transition: background-color 0.3s ease !important;
    }
    .react-joyride__spotlight {
      transition: left 0.4s ease, top 0.4s ease, width 0.4s ease, height 0.4s ease, border-radius 0.3s ease !important;
    }
  `;
  document.head.appendChild(style);
};

// ═══════════════════════════════════════════════════════════════════════════
// PROGRESS RING COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const ProgressRing = ({ current, total }) => {
  const pct = (current / total) * 100;
  const circumference = 2 * Math.PI * 18;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
      <svg width="44" height="44" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r="18" fill="none" stroke="#e5e7eb" strokeWidth="3" />
        <circle
          cx="22" cy="22" r="18"
          fill="none"
          stroke="#F2D857"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 22 22)"
          style={{ transition: 'stroke-dashoffset 0.6s ease-out' }}
        />
      </svg>
      <span style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.7rem', fontWeight: 700, color: '#1f2937',
      }}>
        {current}/{total}
      </span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CUSTOM TOOLTIP
// ═══════════════════════════════════════════════════════════════════════════
const CustomTooltip = ({
  index, step, backProps, closeProps, primaryProps, skipProps, tooltipProps, size,
}) => {
  const { currentPhase, phaseStepCount } = useContext(TourContext);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const isWelcome = index === 0;
  const isLast = index === size - 1;

  // Calculate display step within current phase (skip welcome)
  const displayStep = index;
  const totalDisplay = phaseStepCount - 1; // exclude welcome from count

  const getButtonText = () => {
    if (isWelcome) return currentPhase === 1 ? "Let's Go!" : "Show Me More";
    if (isLast) return currentPhase === 1 ? "I'm Ready!" : "All Done!";
    return 'Next';
  };

  return (
    <div
      {...tooltipProps}
      className="tour-tooltip-enter"
      ref={(el) => {
        // Prevent tooltip from overflowing viewport on mobile
        if (el && isMobile) {
          requestAnimationFrame(() => {
            const rect = el.getBoundingClientRect();
            if (rect.left < 8) {
              el.style.transform = `translateX(${8 - rect.left}px)`;
            } else if (rect.right > window.innerWidth - 8) {
              el.style.transform = `translateX(${window.innerWidth - 8 - rect.right}px)`;
            }
          });
        }
      }}
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: isMobile ? '20px' : '28px',
        boxShadow: '0 25px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '380px',
        width: isMobile ? 'calc(100vw - 32px)' : 'auto',
        minWidth: isMobile ? 'auto' : '300px',
        marginRight: '16px',
        position: 'relative',
        zIndex: 20000,
        overflow: 'hidden',
      }}
    >
      {/* Top accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
        background: 'linear-gradient(90deg, #F2D857, #FFCC00, #FFA500)',
      }} />

      {/* Close button */}
      <button
        {...closeProps}
        style={{
          position: 'absolute', top: '10px', right: '10px',
          background: '#f3f4f6', border: 'none', borderRadius: '50%',
          width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#6b7280', fontSize: '14px', cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#e5e7eb'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
        aria-label="Close"
      >
        ✕
      </button>

      {/* Content */}
      <div className="tour-content-enter" style={{ textAlign: isWelcome ? 'center' : 'left' }}>
        {step.content}
      </div>

      {/* Footer */}
      <div style={{
        marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        {/* Left: skip + progress ring (non-welcome) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            {...skipProps}
            style={{
              color: '#9ca3af', fontSize: '0.8rem', padding: '6px',
              background: 'none', border: 'none', cursor: 'pointer',
            }}
          >
            Skip
          </button>
          {!isWelcome && (
            <ProgressRing current={displayStep} total={totalDisplay} />
          )}
        </div>

        {/* Right: back + next */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {index > 0 && (
            <button
              {...backProps}
              style={{
                color: '#6b7280', fontWeight: 600, padding: '10px 14px',
                fontSize: '0.9rem', background: 'none', border: 'none', cursor: 'pointer',
              }}
            >
              Back
            </button>
          )}
          <button
            {...primaryProps}
            style={{
              backgroundColor: '#F2D857', color: '#1f2937', fontWeight: 700,
              padding: isMobile ? '12px 28px' : '12px 24px',
              borderRadius: '10px', border: 'none', cursor: 'pointer',
              fontSize: '0.95rem',
              transition: 'transform 0.15s, box-shadow 0.15s',
              boxShadow: '0 2px 8px rgba(242, 216, 87, 0.4)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(242, 216, 87, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(242, 216, 87, 0.4)';
            }}
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// LEVEL-UP OVERLAY (Phase 1 → Phase 2 transition)
// ═══════════════════════════════════════════════════════════════════════════
const LevelUpOverlay = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 2200);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div
      className="tour-level-up"
      style={{
        position: 'fixed', inset: 0, zIndex: 20000,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(circle, rgba(242,216,87,0.15) 0%, rgba(0,0,0,0.7) 100%)',
        pointerEvents: 'none',
      }}
    >
      <div style={{ fontSize: '4rem', marginBottom: '16px' }}>&#x1F680;</div>
      <h2 style={{
        color: '#F2D857', fontSize: '2rem', fontWeight: 800,
        textShadow: '0 2px 20px rgba(242,216,87,0.5)',
        margin: 0,
      }}>
        Phase 2 Unlocked!
      </h2>
      <p style={{ color: '#e5e7eb', fontSize: '1rem', marginTop: '8px' }}>
        Let's explore your power features
      </p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// STEP DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════

const phase1Steps = [
  // Step 0: Welcome
  {
    target: 'body',
    content: (
      <div>
        <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>&#x1F3A4;</div>
        <h3 style={{ margin: '0 0 10px', fontSize: '1.3rem', fontWeight: 800, color: '#1f2937' }}>
          Welcome to Your Dashboard!
        </h3>
        <p style={{ margin: 0, lineHeight: 1.7, fontSize: '0.95rem', color: '#4b5563' }}>
          Reviewers on this platform <strong>earn money</strong> reviewing music from independent artists.
          Let's get you set up so artists can find you and start submitting.
        </p>
        <div style={{
          marginTop: '14px', padding: '10px 14px', background: '#FFF9DB',
          borderRadius: '8px', fontSize: '0.85rem', color: '#92400e',
        }}>
          This takes about 60 seconds. You'll learn everything you need to start earning.
        </div>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  // Step 1: Setup Checklist
  {
    target: '[data-tour="setup-checklist"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Your Setup Checklist
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          These 5 steps <strong>unlock your profile</strong> for artist discovery. Until they're done, artists can't find you.
        </p>
        <div style={{ fontSize: '0.85rem', color: '#6b7280', lineHeight: 1.5 }}>
          <span style={{ color: '#16a34a', fontWeight: 600 }}>Most reviewers finish in under 5 minutes.</span>
          {' '}Each green checkmark gets you closer to being visible.
        </div>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  // Step 2: Profile Preview
  {
    target: '[data-tour="profile-preview"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Your Artist-Facing Card
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          This is <strong>exactly</strong> what artists see when browsing reviewers. A strong photo and bio are the difference between getting 50 submissions and getting 0.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#92400e', background: '#FFF9DB', padding: '8px 12px', borderRadius: '6px' }}>
          Use the <strong>Top / Center / Bottom</strong> buttons to frame your photo perfectly on the card.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  // Step 3: Visibility Score
  {
    target: '[data-tour="visibility-score"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Your Visibility Ranking
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          Your <strong>Activity Score</strong> ranks you against every other reviewer. Higher score = artists see you first.
        </p>
        <div style={{ fontSize: '0.85rem', color: '#4b5563', lineHeight: 1.6 }}>
          It's calculated from:<br/>
          <span style={{ color: '#d97706', fontWeight: 600 }}>Session Days ×20</span> +{' '}
          <span style={{ color: '#d97706', fontWeight: 600 }}>Active Minutes ×1</span> +{' '}
          <span style={{ color: '#d97706', fontWeight: 600 }}>Reviews ×5</span>
        </div>
        <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: '#6b7280', fontStyle: 'italic' }}>
          Short daily sessions rank higher than one long session per week — consistency wins.
        </p>
      </div>
    ),
    placement: 'left',
    disableBeacon: true,
  },
  // Step 4: Start Session button
  {
    target: '[data-tour="start-session-btn"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Start a Session
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          When you're ready to accept submissions, tap <strong>"Start Session."</strong> It opens your Session Dashboard where artists' tracks come in live.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#4b5563' }}>
          <strong>This is where you earn.</strong> It stays locked until you connect a payment method — that's step 4 on your checklist.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  // Step 5: Help button (Finale)
  {
    target: '[data-tour="reviewer-help-button"]',
    content: (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '8px' }}>&#x1F389;</div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.15rem', fontWeight: 700, color: '#1f2937' }}>
          You're Ready to Get Started!
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          Complete your checklist, then hit "Start Session" to begin earning.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
          Once you've run your first session, come back here — there's a lot more to explore.
          Tap this <strong>?</strong> button anytime for the full walkthrough.
        </p>
      </div>
    ),
    placement: 'top-end',
    disableBeacon: true,
  },
];

// Tab-step mapping for Phase 2 (auto-switch tabs)
const PHASE2_TAB_MAP = {
  1: 'bio',
  2: 'bio', // social integrations is inside bio tab
  3: 'sessions',
  4: 'emails',
  5: 'notifications',
  6: 'settings',
  7: 'connect-spotify',
};

const phase2Steps = [
  // Step 0: Welcome Back
  {
    target: 'body',
    content: (
      <div>
        <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>&#x26A1;</div>
        <h3 style={{ margin: '0 0 10px', fontSize: '1.3rem', fontWeight: 800, color: '#1f2937' }}>
          Nice — Let's Power Up!
        </h3>
        <p style={{ margin: 0, lineHeight: 1.7, fontSize: '0.95rem', color: '#4b5563' }}>
          You know the basics. Now let's unlock the rest of your dashboard — notifications, your artist mailing list, session settings, and more.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  // Step 1: Bio Tab — target the tab trigger button
  {
    target: '[data-tour="tab-bio"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Bio — Your Profile Info
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          This is where you build trust with artists. Upload a great photo, write a compelling bio that describes your review style, and tag the genres you specialize in.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
          <strong>Pro tip:</strong> Reviewers with detailed bios get 3× more submissions than those with generic ones.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  // Step 2: Social Integrations — target the specific card
  {
    target: '[data-tour="social-integrations"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Social Integrations
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          Link your <strong>Twitch, Instagram, TikTok, Spotify, SoundCloud, and YouTube</strong>. These show on your public profile so artists can follow you everywhere.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
          Artists who already follow you on social media are far more likely to submit and tip.
        </p>
      </div>
    ),
    placement: 'top',
    disableBeacon: true,
  },
  // Step 3: Sessions Tab — target the tab trigger button
  {
    target: '[data-tour="tab-sessions"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Sessions — Your History
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          Every time you start a session and review music, it's logged here — submission count, total earnings, and session status.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
          You can rename sessions and click into any past session to see the full details.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  // Step 4: Emails Tab — target the tab trigger button
  {
    target: '[data-tour="tab-emails"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Emails — Your Artist List
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          Every artist who submits a track gets logged here with their email, track count, and supporter status. This is <strong>your</strong> mailing list — it grows every session.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#92400e', background: '#FFF9DB', padding: '8px 12px', borderRadius: '6px' }}>
          PRO users can export this as a CSV and use it for marketing outside the platform.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  // Step 5: Notifications Tab — target the tab trigger button
  {
    target: '[data-tour="tab-notifications"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Notify — Alert Your Followers
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          Send email blasts to your followers when you start a session. Customize the subject and message, or toggle <strong>auto-notify</strong> so it fires automatically every time.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
          More followers notified = more submissions = more earnings. It's your built-in marketing tool.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  // Step 6: Settings Tab — target the tab trigger button
  {
    target: '[data-tour="tab-settings"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Settings — Your Control Center
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          Manage your <strong>monetization plan</strong>, enable skip-the-line pricing, choose which submission types to accept (Spotify, SoundCloud, YouTube, file uploads), and set submission gates.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
          You can also switch between Free and Pro plans here at any time.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  // Step 7: Spotify Tab — target the tab trigger button
  {
    target: '[data-tour="tab-spotify"]',
    content: (
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
          Spotify — Connect & Play
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          Connect your Spotify account so you can <strong>play submitted tracks directly</strong> in your sessions. No tab-switching or copy-pasting links.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
          Artists submit Spotify links, and you listen right here on the platform — seamless.
        </p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
  },
  // Step 8: Help Button (Finale)
  {
    target: '[data-tour="reviewer-help-button"]',
    content: (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>&#x1F3C6;</div>
        <h4 style={{ margin: '0 0 8px', fontSize: '1.15rem', fontWeight: 700, color: '#1f2937' }}>
          You're a Power User Now!
        </h4>
        <p style={{ margin: '0 0 10px', lineHeight: 1.6, fontSize: '0.9rem', color: '#4b5563' }}>
          You know every tool at your disposal. Now go build your audience, start sessions consistently, and climb the rankings.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
          Tap this <strong>?</strong> anytime to replay either walkthrough.
        </p>
      </div>
    ),
    placement: 'top-end',
    disableBeacon: true,
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// JOYRIDE STYLE CONFIG
// ═══════════════════════════════════════════════════════════════════════════
const getJoyrideStyles = () => ({
  options: {
    primaryColor: '#F2D857',
    textColor: '#1f2937',
    backgroundColor: '#ffffff',
    arrowColor: '#ffffff',
    overlayColor: 'rgba(0, 0, 0, 0.55)',
    zIndex: 15000,
  },
  spotlight: {
    borderRadius: '12px',
    transition: 'left 0.4s ease, top 0.4s ease, width 0.4s ease, height 0.4s ease',
  },
  spotlightLegacy: {
    borderRadius: '12px',
    transition: 'left 0.4s ease, top 0.4s ease, width 0.4s ease, height 0.4s ease',
  },
  overlay: {
    mixBlendMode: 'normal',
    transition: 'background-color 0.3s ease',
  },
  beacon: { display: 'none' },
  beaconInner: { display: 'none' },
  beaconOuter: { display: 'none' },
});

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const ReviewerGuidedTour = ({ user, onTabChange }) => {
  const [currentPhase, setCurrentPhase] = useState(0); // 0=idle, 1=phase1, 2=phase2
  const [runTour, setRunTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const joyrideRef = useRef(null);
  const lastSwitchedTab = useRef(-1);

  const currentSteps = currentPhase === 2 ? phase2Steps : phase1Steps;

  // Inject CSS animations on mount
  useEffect(() => { injectStyles(); }, []);

  // Auto-start Phase 1 if not yet completed (restarts from beginning each time)
  useEffect(() => {
    const phase1Done = localStorage.getItem(PHASE1_COMPLETED_KEY);
    if (!phase1Done) {
      // Delay to ensure DOM is fully rendered before starting Joyride
      const timer = setTimeout(() => {
        setCurrentPhase(1);
        setStepIndex(0);
        requestAnimationFrame(() => {
          setRunTour(true);
        });
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Auto-start Phase 2 when user has sessions and hasn't seen it
  useEffect(() => {
    if (currentPhase !== 0) return; // don't interrupt active tour
    const phase1Done = localStorage.getItem(PHASE1_COMPLETED_KEY);
    const phase2Done = localStorage.getItem(PHASE2_COMPLETED_KEY);
    const totalSessions = user?.total_sessions || user?.totalSessions || 0;

    if (phase1Done && !phase2Done && totalSessions > 0) {
      const timer = setTimeout(() => {
        setShowLevelUp(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, currentPhase]);

  // Handle Level Up overlay completion → start Phase 2
  const handleLevelUpComplete = useCallback(() => {
    setShowLevelUp(false);
    fireConfetti();
    lastSwitchedTab.current = -1;
    setTimeout(() => {
      setCurrentPhase(2);
      setStepIndex(0);
      setRunTour(true);
    }, 500);
  }, []);

  // Ensure the tooltip is fully visible on screen — ONE scroll, no competing movements
  const ensureTooltipVisible = useCallback(() => {
    // Wait for Joyride to render and position the tooltip
    const attemptScroll = (attempt) => {
      const tooltip = document.querySelector('.tour-tooltip-enter');
      if (!tooltip) {
        // Tooltip hasn't rendered yet — retry up to 5 times
        if (attempt < 5) {
          setTimeout(() => attemptScroll(attempt + 1), 150);
        }
        return;
      }

      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const topPad = 20;
      const bottomPad = 30;

      const isAbove = tooltipRect.top < topPad;
      const isBelow = tooltipRect.bottom > viewportHeight - bottomPad;

      if (isAbove || isBelow) {
        // Single smooth scroll — position tooltip so it's centered vertically
        // but biased toward showing more of the tooltip than the target
        tooltip.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    // Start checking after Joyride has had time to render the step
    setTimeout(() => attemptScroll(0), 350);
  }, []);

  // Phase 2: scroll target tab trigger into horizontal view, then ensure tooltip is visible
  useEffect(() => {
    if (!runTour || currentPhase !== 2) return;

    // First, scroll the target tab trigger into horizontal view within the scrollable tab bar
    const step = phase2Steps[stepIndex];
    if (step && step.target !== 'body') {
      const targetEl = document.querySelector(step.target);
      if (targetEl) {
        // scrollIntoView with inline: 'center' handles horizontal scrolling in the tab bar
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    // Then ensure the tooltip is fully visible vertically
    const timer = setTimeout(() => ensureTooltipVisible(), 400);
    return () => clearTimeout(timer);
  }, [stepIndex, runTour, currentPhase, ensureTooltipVisible]);

  // Scroll for Phase 1 steps
  useEffect(() => {
    if (!runTour || currentPhase !== 1) return;
    const timer = setTimeout(() => ensureTooltipVisible(), 200);
    return () => clearTimeout(timer);
  }, [stepIndex, runTour, currentPhase, ensureTooltipVisible]);

  // Elevate the current target element above the overlay so content is visible through the spotlight
  useEffect(() => {
    const steps = currentPhase === 2 ? phase2Steps : phase1Steps;
    const step = steps[stepIndex];

    if (!runTour || !step || step.target === 'body') {
      // Remove class from all elements when tour isn't active
      document.querySelectorAll('.tour-spotlight-active').forEach(el => {
        el.classList.remove('tour-spotlight-active');
      });
      document.querySelectorAll('.tour-tabbar-elevated').forEach(el => {
        el.classList.remove('tour-tabbar-elevated');
      });
      return;
    }

    // Remove from previous target
    document.querySelectorAll('.tour-spotlight-active').forEach(el => {
      el.classList.remove('tour-spotlight-active');
    });
    document.querySelectorAll('.tour-tabbar-elevated').forEach(el => {
      el.classList.remove('tour-tabbar-elevated');
    });

    // Add to current target
    const el = document.querySelector(step.target);
    if (el) {
      el.classList.add('tour-spotlight-active');
      // If targeting a tab trigger inside the scrollable tab bar, elevate the entire container
      const tabContainer = el.closest('.reviewer-dashboard-tabs');
      if (tabContainer) {
        tabContainer.classList.add('tour-tabbar-elevated');
      }
    }

    return () => {
      document.querySelectorAll('.tour-spotlight-active').forEach(el => {
        el.classList.remove('tour-spotlight-active');
      });
      document.querySelectorAll('.tour-tabbar-elevated').forEach(el => {
        el.classList.remove('tour-tabbar-elevated');
      });
    };
  }, [stepIndex, runTour, currentPhase]);

  // Joyride callback handler
  const handleCallback = useCallback((data) => {
    const { status, action, index, type } = data;

    if (action === ACTIONS.CLOSE) {
      // User closed/dismissed the tour — do NOT mark as completed
      setRunTour(false);
      setStepIndex(0);
      setCurrentPhase(0);
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      // Phase 2: check if next step needs a tab switch
      if (currentPhase === 2) {
        const nextTab = PHASE2_TAB_MAP[nextIndex];
        const currentTab = PHASE2_TAB_MAP[index];

        if (nextTab && nextTab !== currentTab && onTabChange) {
          // Pause tour → switch tab → scroll tab trigger into view → advance step → resume
          setRunTour(false);
          onTabChange(nextTab);
          setTimeout(() => {
            // Scroll the next step's target tab trigger into horizontal view
            const nextStep = phase2Steps[nextIndex];
            if (nextStep && nextStep.target !== 'body') {
              const nextTarget = document.querySelector(nextStep.target);
              if (nextTarget) {
                nextTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
              }
            }
            setStepIndex(nextIndex);
            requestAnimationFrame(() => setRunTour(true));
          }, 800);
          return;
        }
      }

      setStepIndex(nextIndex);
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      // Phase 2: if target not found, maybe the tab hasn't switched yet — try switching
      if (currentPhase === 2) {
        const nextTab = PHASE2_TAB_MAP[nextIndex];
        if (nextTab && onTabChange) {
          setRunTour(false);
          onTabChange(nextTab);
          setTimeout(() => {
            // Scroll the target into horizontal view
            const nextStep = phase2Steps[nextIndex];
            if (nextStep && nextStep.target !== 'body') {
              const nextTarget = document.querySelector(nextStep.target);
              if (nextTarget) {
                nextTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
              }
            }
            setStepIndex(nextIndex);
            requestAnimationFrame(() => setRunTour(true));
          }, 800);
          return;
        }
      }

      setStepIndex(nextIndex);
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      setStepIndex(0);

      // Only mark as completed when user finishes ALL steps (not on skip)
      if (status === STATUS.FINISHED) {
        if (currentPhase === 1) {
          localStorage.setItem(PHASE1_COMPLETED_KEY, 'true');
          fireConfetti();
        }
        if (currentPhase === 2) {
          localStorage.setItem(PHASE2_COMPLETED_KEY, 'true');
          fireConfetti();
        }
      }
      setCurrentPhase(0);
    }
  }, [currentPhase, onTabChange]);

  // Manual start from help button
  const startPhase = useCallback((phase) => {
    setRunTour(false);
    setCurrentPhase(phase);
    setStepIndex(0);
    lastSwitchedTab.current = -1;
    // If starting phase 2, switch to bio tab first
    if (phase === 2 && onTabChange) {
      onTabChange('bio');
    }
    setTimeout(() => {
      requestAnimationFrame(() => setRunTour(true));
    }, 150);
  }, [onTabChange]);

  // Help button menu state
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const phase1Done = localStorage.getItem(PHASE1_COMPLETED_KEY);
  const phase2Done = localStorage.getItem(PHASE2_COMPLETED_KEY);

  // Context value for the tooltip to read
  const tourContextValue = useMemo(() => ({
    currentPhase,
    phaseStepCount: currentSteps.length,
  }), [currentPhase, currentSteps.length]);

  return (
    <TourContext.Provider value={tourContextValue}>
      <Joyride
        ref={joyrideRef}
        steps={currentSteps}
        run={runTour}
        stepIndex={stepIndex}
        continuous
        showProgress={false}
        showSkipButton
        scrollToFirstStep={false}
        disableOverlayClose={false}
        spotlightClicks={false}
        disableScrolling={true}
        scrollOffset={150}
        spotlightPadding={10}
        callback={handleCallback}
        styles={getJoyrideStyles()}
        tooltipComponent={CustomTooltip}
        locale={{
          back: 'Back',
          close: '✕',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip',
        }}
        floaterProps={{
          disableAnimation: false,
          hideArrow: false,
          offset: 12,
          styles: {
            floater: {
              filter: 'none',
              transition: 'opacity 0.35s ease, left 0.4s ease, top 0.4s ease, transform 0.4s ease',
            },
            arrow: {
              length: 10,
              spread: 16,
            },
          },
        }}
      />

      {/* Level Up Overlay */}
      {showLevelUp && <LevelUpOverlay onComplete={handleLevelUpComplete} />}

      {/* Floating Help Button */}
      {(!runTour || stepIndex === currentSteps.length - 1) && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', bottom: 'max(80px, calc(env(safe-area-inset-bottom, 16px) + 64px))', right: '16px', zIndex: 9998 }}
        >
          {/* Menu popup */}
          {showMenu && (
            <div
              style={{
                position: 'absolute', bottom: '60px', right: 0,
                backgroundColor: '#fff', borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.2)', padding: '8px',
                minWidth: '200px',
                animation: 'tourFadeSlideUp 0.2s ease-out forwards',
              }}
            >
              <button
                onClick={() => { setShowMenu(false); startPhase(1); }}
                data-testid="tour-phase1-btn"
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '10px 14px', border: 'none', background: 'none',
                  cursor: 'pointer', borderRadius: '8px', fontSize: '0.9rem',
                  color: '#1f2937', fontWeight: 500,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              >
                <span style={{ marginRight: '8px' }}>&#x1F3AF;</span>
                Get Discovered
                {phase1Done && <span style={{ marginLeft: '6px', color: '#16a34a', fontSize: '0.75rem' }}>&#x2713;</span>}
              </button>
              <button
                onClick={() => { setShowMenu(false); startPhase(2); }}
                data-testid="tour-phase2-btn"
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '10px 14px', border: 'none', background: 'none',
                  cursor: 'pointer', borderRadius: '8px', fontSize: '0.9rem',
                  color: '#1f2937', fontWeight: 500,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              >
                <span style={{ marginRight: '8px' }}>&#x26A1;</span>
                Power Up
                {phase2Done && <span style={{ marginLeft: '6px', color: '#16a34a', fontSize: '0.75rem' }}>&#x2713;</span>}
              </button>
            </div>
          )}

          {/* The ? button */}
          <button
            data-tour="reviewer-help-button"
            data-testid="reviewer-tour-help-btn"
            onClick={runTour ? undefined : () => setShowMenu(!showMenu)}
            aria-label="Start guided walkthrough"
            style={{
              width: '52px', height: '52px', borderRadius: '50%',
              backgroundColor: '#F2D857', border: 'none',
              boxShadow: '0 4px 20px rgba(242, 216, 87, 0.5)',
              cursor: runTour ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '22px', fontWeight: 800, color: '#1f2937',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!runTour) {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(242, 216, 87, 0.6)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(242, 216, 87, 0.5)';
            }}
            title="Take a guided walkthrough"
          >
            ?
          </button>
        </div>
      )}
    </TourContext.Provider>
  );
};

export default ReviewerGuidedTour;
export { PHASE1_COMPLETED_KEY, PHASE1_SHOWN_KEY, PHASE2_COMPLETED_KEY };
